import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Quote } from 'lucide-react';
import Reveal from './Reveal';
import { SectionMarker } from './Gap';

// Testimonials pulled verbatim from empactstudio.com. Swap freely as new ones arrive.
const testimonials = [
  {
    quote:
      'I have known Vijay for over 25 years and he has a clear grasp of how conventional, experiential and digital technologies work within physical and digital spaces. His ability to plan and execute across all layers is a real asset on complex projects and inputs improve both creative and operational outcomes.',
    name: 'Mr. Viraf Sarkari',
    position: 'Director',
    company: 'Wizcraft',
  },
  {
    quote:
      'I’ve had the privilege of working with Vijay across multiple high-stakes productions, and his ability to translate creative ambition into flawless technical execution is truly exceptional. He brings vision to life with precision, remains solutions-driven at every stage, and consistently pushes the envelope of what’s possible. Above all, his reliability and calm under pressure make him a great partner to have in any complex project.',
    name: 'Samit Garg',
    position: 'Co-founder and Managing Director',
    company: 'E Factor Experiences Ltd',
  },
  {
    quote:
      'Mr. Vijay Sabhlok comes with immense knowledge and experience with regards to every technical bit of an event. He is an inspiration to many and particularly to me. He has helped us create some of the most memorable event experiences. I can say without a doubt that he is the best Technical Strategist in India.',
    name: 'Mr. Vibhore Khandelwal',
    position: 'Founder & Creative Head',
    company: 'Creative Factory',
  },
  {
    quote:
      'Vijay Sabhlok stands as a true pioneer in the experiential space. His work continues to set new standards. Collaborating with Vijay has always been seamless and inspiring. His strategic clarity and technical mastery, along with his understanding of what works in a given space, is unparalleled. He delivers solutions with clarity and control, bringing a sense of calm to even the most complex projects.',
    name: 'Mr. Chetan Vohra',
    position: 'Director',
    company: 'Wedding Line',
  },
  {
    quote:
      'Vijay and his team have been fantastic technical consultants to us across many projects. Their deep knowledge has helped us plan complex infrastructure-heavy productions, with absolute safety, while ensuring the desired impact.',
    name: 'Mr. Tarun Aggarwal',
    position: 'Co-Founder and CEO',
    company: 'Communique India',
  },
  {
    quote:
      'Vijay has been a key part of many of Evoke’s signature and award-winning projects. His ability to bring together leadership, creativity and imagination is truly rare. What I value most about working with him is his ownership and dedication - he has a way of taking an idea and elevating it beyond what you imagined. Whenever Vijay is part of a project, I genuinely feel, “We’re in safe hands. Nothing can go wrong.”',
    name: 'Mr. Cilvin Jayakumar',
    position: 'Founder',
    company: 'Evoke Media',
  },
];

const AUTO_MS = 7000;

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const timer = useRef(null);

  const go = useCallback(
    (next) => {
      setDir(next > index || (index === testimonials.length - 1 && next === 0) ? 1 : -1);
      setIndex(next);
    },
    [index],
  );

  const next = useCallback(() => {
    setDir(1);
    setIndex((i) => (i + 1) % testimonials.length);
  }, []);

  const prev = useCallback(() => {
    setDir(-1);
    setIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
  }, []);

  useEffect(() => {
    timer.current = setInterval(next, AUTO_MS);
    return () => clearInterval(timer.current);
  }, [next, index]);

  const t = testimonials[index];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative section-pad border-t border-[var(--border)]"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal>
          <SectionMarker index="VII" label="IN THEIR WORDS" className="label-gap" />
        </Reveal>
        <Reveal>
          <p className="label label-gap -mt-3 md:-mt-4">Decade of trust for the founders</p>
        </Reveal>

        <div
          className="relative rounded-2xl px-5 py-10 sm:px-6 sm:py-12 md:px-16 md:py-14 overflow-hidden border border-[rgba(255,255,255,0.12)]"
          style={{ backgroundColor: 'var(--brand-blue)' }}
        >
          {/* blueprint grid */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.14]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
          <span className="pointer-events-none absolute top-5 left-5 w-6 h-6 border-l-2 border-t-2 border-[var(--accent)]/70" />
          <span className="pointer-events-none absolute bottom-5 right-5 w-6 h-6 border-r-2 border-b-2 border-[var(--accent)]/70" />

          <Quote
            className="text-[var(--accent)] mt-7 md:mt-6 mb-4 md:mb-6 relative w-10 h-10 md:w-14 md:h-14"
            strokeWidth={1.4}
          />

          {/* Quote text - fixed-height box (generous, never clips); only the text crossfades */}
          <div className="relative grid min-h-[320px] sm:min-h-[300px]">
            <AnimatePresence>
              <motion.p
                key={index}
                data-testid="testimonial-quote"
                className="[grid-area:1/1] w-full text-white font-light text-[clamp(1.15rem,2.2vw,1.8rem)] leading-[1.45] max-w-[72ch]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
              >
                {t.quote}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Name + designation - fixed position, same on every card */}
          <div className="relative h-[72px] md:h-[56px] mt-6">
            <AnimatePresence>
              <motion.div
                key={index}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
              >
                <p className="font-display font-medium text-[clamp(1.1rem,2vw,1.6rem)] text-[var(--accent)]">
                  {t.name}
                </p>
                <p className="label !text-[rgba(255,255,255,0.7)] mt-2 md:whitespace-nowrap">
                  <span className="block md:inline">{t.position}</span>
                  {t.company && (
                    <span className="block md:inline whitespace-nowrap">
                      <span className="hidden md:inline"> · </span>
                      {t.company}
                    </span>
                  )}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* controls */}
          <div className="relative mt-3 flex items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to testimonial ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => go(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${i === index ? 'w-8 bg-[var(--accent)]' : 'w-2 bg-white/30 hover:bg-white/50'}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label="Previous testimonial"
                data-testid="testimonial-prev"
                onClick={prev}
                className="w-12 h-12 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-[var(--accent)] hover:text-[#0A0D17] hover:border-[var(--accent)] transition-all"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                aria-label="Next testimonial"
                data-testid="testimonial-next"
                onClick={next}
                className="w-12 h-12 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-[var(--accent)] hover:text-[#0A0D17] hover:border-[var(--accent)] transition-all"
              >
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
